// Client-side pre-submit check mirroring the backend's SKILL.md YAML
// frontmatter contract (see `parse_skill_frontmatter` in
// src/agenthub/schemas/skills.py). This is a UX nicety only — the backend
// is authoritative and re-validates on every create/publish request. Only
// the checks needed to produce a clear inline error are duplicated here
// (no YAML parser pulled in for a two-delimiter split + key lookup).

const XML_TAG_RE = /<[^>]+>/;
const NAME_CHARSET_RE = /^[a-z0-9-]+$/;
const RESERVED_WORDS = ['anthropic', 'claude'];

const FRONTMATTER_HINT =
  'SKILL.md must start with `---` frontmatter containing `name` and `description` matching the required format.';

/** Returns an error message if `skillMdContent` fails the frontmatter contract, or `null` if valid. */
export function validateSkillFrontmatter(skillMdContent: string): string | null {
  const match = /^---\n([\s\S]*?)\n---/.exec(skillMdContent);
  if (!match) return FRONTMATTER_HINT;

  const fields: Record<string, string> = {};
  for (const line of match[1].split('\n')) {
    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) continue;
    fields[line.slice(0, colonIndex).trim()] = line.slice(colonIndex + 1).trim();
  }

  const name = fields.name;
  const description = fields.description;
  if (!name || !description) return FRONTMATTER_HINT;

  if (name.length > 64) return 'SKILL.md frontmatter: `name` must be 64 characters or fewer.';
  if (!NAME_CHARSET_RE.test(name)) {
    return 'SKILL.md frontmatter: `name` must contain only lowercase letters, numbers, and hyphens.';
  }
  if (XML_TAG_RE.test(name)) return 'SKILL.md frontmatter: `name` cannot contain XML tags.';
  const lowerName = name.toLowerCase();
  if (RESERVED_WORDS.some((word) => lowerName.includes(word))) {
    return 'SKILL.md frontmatter: `name` cannot contain "anthropic" or "claude".';
  }

  if (description.length > 1024) return 'SKILL.md frontmatter: `description` must be 1024 characters or fewer.';
  if (XML_TAG_RE.test(description)) return 'SKILL.md frontmatter: `description` cannot contain XML tags.';

  return null;
}
